import { formatCents } from "@/lib/money";

import {
  BarList,
  ReportSection,
  ReportTable,
  Stat,
  StatGrid,
} from "../ReportPrimitives";
import type { GeographyReport } from "../reportTables";

/**
 * Where the work and the money are.
 *
 * Customer city and job-site city are reported SEPARATELY because for a
 * service business they genuinely differ, a property manager billed in
 * Toronto can have jobs across four suburbs, and averaging them together
 * would produce a territory picture that is wrong in both directions.
 */
export const GeographyPanel = ({ data }: { data: GeographyReport }) => {
  const totalRevenue = data.by_city.reduce(
    (sum, row) => sum + row.revenue_cents,
    0,
  );
  const known = data.by_city.filter((row) => row.city !== "Unknown");

  return (
    <div className="flex flex-col gap-4">
      <StatGrid>
        <Stat
          label="Cities"
          value={String(known.length)}
          helper="With at least one customer"
        />
        <Stat
          label="Revenue"
          value={formatCents(totalRevenue)}
          helper="Collected in this period"
          tone="positive"
        />
        <Stat
          label="Job sites"
          value={String(data.jobs_by_city.reduce((s, r) => s + r.jobs, 0))}
          helper="Jobs created in period"
        />
        <Stat
          label="Top city"
          value={known[0]?.city ?? "n/a"}
          helper={
            known[0] ? formatCents(known[0].revenue_cents) : "No data yet"
          }
        />
      </StatGrid>

      <div className="grid gap-4 lg:grid-cols-2 [&>*]:min-w-0">
        <ReportSection
          title="Revenue by customer city"
          subtitle="Where the billing address is"
        >
          <ReportTable
            columns={["City", "Contacts", "Jobs", "Revenue"]}
            align={["left", "right", "right", "right"]}
            rows={data.by_city.map((row) => [
              row.state ? `${row.city}, ${row.state}` : row.city,
              row.contacts,
              row.jobs,
              formatCents(row.revenue_cents),
            ])}
            empty="No contacts with a city recorded yet."
          />
        </ReportSection>

        <ReportSection
          title="Jobs by site city"
          subtitle="Where the van actually goes"
        >
          <BarList
            items={data.jobs_by_city.map((row) => ({
              label: row.city,
              value: row.jobs,
              display: String(row.jobs),
              sub: row.completed > 0 ? `${row.completed} completed` : undefined,
            }))}
            empty="No jobs with a site city in this period."
          />
        </ReportSection>
      </div>

      {data.by_city.some((row) => row.city === "Unknown") ? (
        <p className="text-[12px] text-muted-foreground">
          Contacts without a city are grouped as “Unknown”. Filling in city on
          the contact record sharpens this report.
        </p>
      ) : null}
    </div>
  );
};
